// Libraries
import { SearchIcon } from '@chakra-ui/icons';
import { Box, Flex, MenuIcon, Text } from '@chakra-ui/react';
import {
  ControlProps,
  GroupBase,
  InputProps,
  OptionProps,
  SelectComponentsConfig,
  ValueContainerProps,
  chakraComponents,
} from 'chakra-react-select';
import { Children, Dispatch, ReactElement, SetStateAction, KeyboardEvent } from 'react';
// Components
import { RoundedCheckIcon } from '@atoms/index';
// Utils
import text from '@translations/en.json';
// Types
import { Option } from '../Dropdown.types';

/**
 * The `Control` component is a custom implementation of the `react-select` control component.
 * It overrides the default focus behavior of the control component by always making it unfocused.
 *
 * @template OptionType - The type of each option in the dropdown.
 * @template IsMulti - A boolean value that determines whether multiple options can be selected.
 *
 * @component
 * @param {ControlProps<OptionType, IsMulti, GroupBase<OptionType>>} props - The properties that define the `Control` component.
 *
 * @returns {ReactElement} Returns a control component which always has a non-focused visual state.
 */
const Control = <OptionType extends Option, IsMulti extends boolean>(
  props: ControlProps<OptionType, IsMulti, GroupBase<OptionType>>
) => <chakraComponents.Control {...props} isFocused={false} />;

/**
 * The `Input` component is a higher-order function that returns a functional component.
 * This functional component is a custom implementation of the `react-select` input component.
 * It automatically focuses the input and closes the dropdown on the 'Escape' key press.
 *
 * @template OptionType - The type of each option in the dropdown.
 * @template IsMulti - A boolean value that determines whether multiple options can be selected.
 *
 * @component
 * @param {Dispatch<SetStateAction<boolean>>} setIsOpen - The state dispatch function to open and close the dropdown.
 *
 * @returns {FunctionComponent<InputProps<OptionType, IsMulti, GroupBase<OptionType>>>} Returns a custom input component which automatically gets focused and closes the dropdown on 'Escape' key press.
 */
const Input =
  <OptionType extends Option, IsMulti extends boolean>(setIsOpen: Dispatch<SetStateAction<boolean>>) =>
  (props: InputProps<OptionType, IsMulti, GroupBase<OptionType>>) => {
    /**
     * Function to handle a keydown event on an HTML input element.
     *
     * @function
     * @param event - The keyboard event. It is inferred to be of type `KeyboardEvent<HTMLInputElement>`.
     */
    const handleKeyDown = ({ key }: KeyboardEvent<HTMLInputElement>) => {
      if (key === 'Escape') setIsOpen(false);
    };

    /**
     * Function to get a reference to an HTML input element and set focus on it.
     *
     * @function
     * @param inputElement - The input element. It is of type `HTMLInputElement` or `null`.
     */
    const innerRef = (inputElement: HTMLInputElement | null) => {
      inputElement?.focus();
    };

    return <chakraComponents.Input {...props} innerRef={innerRef} onKeyDown={handleKeyDown} />;
  };

/**
 * The `ValueContainer` component is a custom implementation of the `react-select` ValueContainer component.
 * It contains a `Search` label and a `SearchIcon` which only appears when the input is empty.
 *
 * @template OptionType - The type of each option in the dropdown.
 * @template IsMulti - A boolean value that determines whether multiple options can be selected.
 *
 * @component
 * @param {ValueContainerProps<OptionType, IsMulti, GroupBase<OptionType>>} props - Props supplied to the ValueContainer component.
 *
 * @returns {ReactElement} Returns a custom value container component which includes a 'Search' label and a 'SearchIcon', which disappear when input is not empty.
 */
const ValueContainer = <OptionType extends Option, IsMulti extends boolean>(
  props: ValueContainerProps<OptionType, IsMulti, GroupBase<OptionType>>
) => {
  const inputElement = Children.toArray(props.children).at(-1) as ReactElement;
  const isSearching = inputElement?.props?.value;
  return (
    <chakraComponents.ValueContainer {...props}>
      <Box position='relative'>
        {!isSearching && (
          <Flex position='absolute' h='full' alignItems='center'>
            <Flex ml={1} gap={1} align='center'>
              <SearchIcon color='grey.100' />
              <Text fontSize='l' color='grey.100'>
                {text.search}
              </Text>
            </Flex>
          </Flex>
        )}
        {inputElement}
      </Box>
    </chakraComponents.ValueContainer>
  );
};

/**
 * The `ValueContainer` component is a custom implementation of the `react-select` ValueContainer component.
 * It contains a `Search` label and a `SearchIcon` which only appears when the input is empty.
 *
 * @template OptionType - The type of each option in the dropdown.
 * @template IsMulti - A boolean value that determines whether multiple options can be selected.
 *
 * @component
 * @param {ValueContainerProps<OptionType, IsMulti, GroupBase<OptionType>>} props - Props supplied to the ValueContainer component.
 *
 * @returns {ReactElement} Returns a custom value container component which includes a 'Search' label and a 'SearchIcon', which disappear when input is not empty.
 */
const OptionElement = <OptionType extends Option, IsMulti extends boolean>(
  props: OptionProps<OptionType, IsMulti, GroupBase<OptionType>>
) => {
  const borderColor = props.isSelected ? 'tealishBlue' : 'grey.20';
  const backgroundColor = props.isSelected ? 'tealishBlue' : 'transparent';
  const color = props.isSelected ? 'white' : 'transparent';

  return (
    <chakraComponents.Option {...props}>
      <Flex as='button' flex='1' align='center' gap={2} ref={props.innerRef} {...props.innerProps}>
        <MenuIcon
          sx={{
            boxSize: 4,
            borderRadius: 'sm',
            border: 'section',
            borderColor,
            backgroundColor,
          }}
        >
          <RoundedCheckIcon boxSize={3} color={color} />
        </MenuIcon>
        <Text color='darkGray.100' fontSize='m'>
          {props.children}
        </Text>
      </Flex>
    </chakraComponents.Option>
  );
};

/**
 * The `dropdownComponents` function generates a set of custom components for a `react-select` Select component.
 * It allows for a high level of customization of the dropdown behavior and appearance.
 *
 * @template OptionType - The type of each option in the dropdown.
 * @template IsMulti - A boolean value that determines whether multiple options can be selected.
 *
 * @function
 * @param {Dispatch<SetStateAction<boolean>>} setIsOpen - A function that sets the state for the dropdown's open/closed status.
 *
 * @returns {Partial<SelectComponentsConfig<OptionType, IsMulti, GroupBase<OptionType>>>} - An object containing the custom components to be used in the dropdown.
 */
export const dropdownComponents = <OptionType extends Option, IsMulti extends boolean>(
  setIsOpen: Dispatch<SetStateAction<boolean>>
): Partial<SelectComponentsConfig<OptionType, IsMulti, GroupBase<OptionType>>> => ({
  Control,
  Input: Input(setIsOpen),
  ValueContainer,
  DropdownIndicator: null,
  IndicatorSeparator: null,
});

/**
 * The `multiComponents` function generates a set of custom components for a multi-select `react-select` Select component.
 * It allows for a high level of customization of the multi-select dropdown behavior and appearance.
 *
 * @template OptionType - The type of each option in the dropdown.
 * @template IsMulti - A boolean value that determines whether multiple options can be selected.
 *
 * @function
 *
 * @returns {Partial<SelectComponentsConfig<OptionType, IsMulti, GroupBase<OptionType>>>} - An object containing the custom components to be used in the dropdown.
 */
export const multiComponents = <OptionType extends Option, IsMulti extends boolean>(): Partial<
  SelectComponentsConfig<OptionType, IsMulti, GroupBase<OptionType>>
> => ({
  Option: OptionElement,
});
